import React, { useState } from "react";
import {
  Table, TableRow, TableCell, TableHead, TableBody,
  Typography, TablePagination, Chip, Box, Select,
  MenuItem, FormControl, InputLabel, TableContainer,
  Paper, IconButton, Tooltip, TextField
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { useEffectAsync } from "../reactHelper";
import { getWithExpiry } from "../common/util/localstorage";
import SearchHeader, { filterByKeyword } from "../common/components/SearchHeader";
import { useDispatch, useSelector } from "react-redux";
import { shippingsActions } from "../store";
import TableShimmer from "../common/components/TableShimmer";
import { useNavigate } from "react-router-dom";
import ErrorDialog from "../common/components/ErrorDialog";
import CollectionFab from "../settings/CollectionFab";
import CollectionActions from "../common/components/CollectionActions";

/* -------------------- COLORS -------------------- */

const statutColors = {
  en_attente: "#FFA500",
  livre: "#008000",
  retour: "#800080",
};

/* -------------------- COMPONENT -------------------- */

const ShippingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [opening, setOpening] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const _shippings = useSelector((state) => state.shippings.items);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* -------------------- FETCH -------------------- */

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_URL_NAME}/shipping`,
        {
          headers: {
            Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
          },
        }
      );

      if (!res.ok) {
        if (res.status === 401) navigate("/login");
        else {
          setErrorMsg(await res.text());
          setOpening(true);
        }
        return;
      }

      const data = await res.json();
      dispatch(shippingsActions.setItems(data));
    } finally {
      setLoading(false);
    }
  }, []);

  /* -------------------- FILTER -------------------- */

  const statuts = ["all", ...new Set(_shippings.map((s) => s.statut))];

  const filtered = _shippings
    .filter(filterByKeyword(searchKeyword))
    .filter((s) => filterStatut === "all" || s.statut === filterStatut)
    .filter((s) => {
      if (!dateFrom && !dateTo) return true;
      const date = new Date(s.shipping_date);
      if (dateFrom && date < new Date(dateFrom)) return false;
      if (dateTo && date > new Date(dateTo)) return false;
      return true;
    });

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* -------------------- PDF -------------------- */

  const exportPDF = () => {
    const doc = new jsPDF("landscape");

    doc.setFontSize(14);
    doc.text("Rapport Shippings", 14, 15);

    doc.setFontSize(10);
    doc.text(
      `Statut: ${filterStatut} | Date: ${dateFrom || "-"} → ${dateTo || "-"}`,
      14,
      22
    );

    autoTable(doc, {
      startY: 28,
      head: [[
        "#", "Produit", "Statut", "Date envoi",
        "Date retour", "Payée", "Ajouté par", "Commentaire"
      ]],
      body: filtered.map((s, i) => [
        i + 1,
        s.produit?.reference || "-",
        s.statut,
        s.shipping_date ? new Date(s.shipping_date).toLocaleDateString() : "-",
        s.return_date ? new Date(s.return_date).toLocaleDateString() : "-",
        s.payee ? "Oui" : "Non",
        s.ajoute_par || "-",
        s.commentaire || "-",
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [0, 35, 85] },
    });

    doc.save("shippings.pdf");
  };

  /* -------------------- RENDER -------------------- */

  return (
    <>
      {/* TITLE */}
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 3,
          fontWeight: 800,
          background: "linear-gradient(90deg, #002355, #0081f9)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Shippings
      </Typography>

      {/* FILTER BAR */}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
      >
        <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />

        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
            >
              {statuts.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            type="date"
            label="Date début"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />

          <TextField
            type="date"
            label="Date fin"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />

          <Tooltip title="Exporter PDF">
            <IconButton
              onClick={exportPDF}
              sx={{
                background: "linear-gradient(135deg,#d32f2f,#ff5252)",
                color: "#fff",
              }}
            >
              <PictureAsPdfIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#002355" }}>
              {[
                "#",
                "PRODUIT",
                "STATUT",
                "DATE ENVOI",
                "DATE RETOUR",
                "PAYÉE",
                "AJOUTÉ PAR",
                "COMMENTAIRE",
                "ACTIONS",
              ].map((h) => (
                <TableCell key={h} sx={{ color: "#fff", fontWeight: 700 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && paginated.length ? (
              paginated.map((s, i) => (
                <TableRow key={s._id} hover>
                  <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                  <TableCell>{s.produit?.reference}</TableCell>

                  <TableCell>
                    <Chip
                      label={s.statut}
                      size="small"
                      sx={{
                        color: "#fff",
                        backgroundColor: statutColors[s.statut] || "#ccc",
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    {s.shipping_date
                      ? new Date(s.shipping_date).toLocaleDateString()
                      : "-"}
                  </TableCell>

                  <TableCell>
                    {s.return_date
                      ? new Date(s.return_date).toLocaleDateString()
                      : "-"}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={s.payee ? "✔ Oui" : "✖ Non"}
                      size="small"
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        backgroundColor: s.payee ? "#2e7d32" : "#d32f2f",
                      }}
                    />
                  </TableCell>                  <TableCell>{s.ajoute_par || "-"}</TableCell>
                  <TableCell>{s.commentaire || "-"}</TableCell>

                  <TableCell padding="none">
                    <CollectionActions
                      itemId={s._id}
                      editPath="/settings/shipping"
                      endpoint="shipping"
                      action={shippingsActions}
                      removable
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableShimmer columns={9} endAction />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINATION */}
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(+e.target.value);
          setPage(0);
        }}
      />

      <CollectionFab editPath="/settings/shipping" />

      <ErrorDialog open={opening} errorMsg={errorMsg} />
    </>
  );
};

export default ShippingsPage;